import { AppMessage } from '@spx-portal/shared-components';
import { getCurrentStationId, isAdmin } from '@spx-portal/shared-utils';
import dayjs, { Dayjs } from 'dayjs';
import isUndefined from 'lodash/isUndefined';
import uniqBy from 'lodash/uniqBy';
import * as React from 'react';
import { Form, Input, InputNumber, Layout, message, Modal, Radio, Select, TimePicker } from 'ssc-ui-react';

import { $gt } from '@/common/appCtx';
import { useCheckGrayStation } from '@/store/gray-station';

import { transformCreateShiftParams, transformUpdateShiftParams } from '../adapter';
import { createShift, updateShift } from '../api';
import { useFields } from '../fields';
import style from '../index.module.less';
import { useStationList } from './use-station-list';
interface ShiftModalProps {
  onSuccess?: () => void;
}

type FillMode = 'Auto Fill' | 'Manual Input';

const DEFAULT_MAX_CROSS_DAY = 6;
const ADMIN_STATION_FLAG = 0;

export const useShiftModal = (props?: ShiftModalProps) => {
  const [fillMode, setFillMode] = React.useState<FillMode>('Auto Fill');
  const [mode, setMode] = React.useState<'create' | 'edit'>('create');
  const [visible, setVisible] = React.useState(false);
  const [editData, setEditData] = React.useState<Record<string, any>>();
  const [editStation, setEditStation] = React.useState<{station_name: string; station_id: number}>();
  const [form] = Form.useForm();
  const { staffTypeOption } = useFields();
  const { options: stationOptions, loading: stationLoading, getStationList, onStationDropdownVisibleChangeRef } = useStationList();
  const grayStationData = useCheckGrayStation.use.grayStationData();
  const startTime = Form.useWatch('start_time', form);
  const endTime = Form.useWatch('end_time', form);
  const crossDay = Form.useWatch('cross_day', form);
  const stationIdList = Form.useWatch('station_id_list', form);

  React.useEffect(() => {
    if (mode === 'edit') {
      setFillMode('Manual Input');
    }
    if (mode === 'create') {
      if (visible) {
        onStationDropdownVisibleChangeRef.current(true);
      }
      setFillMode('Auto Fill');
    }
  }, [mode, onStationDropdownVisibleChangeRef, visible]);

  const handleCreateShift = React.useCallback(async () => {
    try {
      await form.validateFields();
    } catch (error) {
      return Promise.reject(error);
    }
    try {
      const params = form.getFieldsValue();
      await createShift(transformCreateShiftParams({
        ...params,
        // send station_id_list to backend because it's not in the form
        station_id_list: isAdmin() ? params.station_id_list : [getCurrentStationId()],
      }));
      message.success($gt('Create shift successfully'));
      setVisible(false);
      props?.onSuccess?.();
    } catch (e: any) {
      console.error(e);
      AppMessage.error(e.retcode, e.message);
    }
  }, [form, props]);

  const handleEditShift = React.useCallback(async () => {
    try {
      await form.validateFields();
    } catch (error) {
      return Promise.reject(error);
    }
    try {
      const params = form.getFieldsValue();
      await updateShift(transformUpdateShiftParams({
        ...params,
        slot_id: editData?.slot_id,
        // send station_id_list to backend because it's not in the form
        station_id_list: isAdmin() ? params.station_id_list : [getCurrentStationId()],
      }));
      message.success($gt('Edit shift successfully'));
      setVisible(false);
      props?.onSuccess?.();
    } catch (e: any) {
      console.error(e);
      AppMessage.error(e.retcode, e.message);
    }
  }, [editData?.slot_id, form, props]);

  const getShiftType = React.useCallback((startTime?: Dayjs) => {
    const hour = startTime?.hour();
    const minute = startTime?.minute();
    if (isUndefined(hour) || isUndefined(minute)) return '';
    if (hour >= 6 && hour <= 15) {
      if (hour === 6 && minute === 0) {
        return $gt('M');
      }
      if (hour === 15 && minute > 0) {
        return $gt('N');
      }
      return $gt('D');
    }
    if (hour > 15 && hour <= 23) {
      if (hour === 23 && minute > 0) {
        return $gt('M');
      }
      return $gt('N');
    }
    return $gt('M');
  }, []);

  const autoGenerateShiftName = React.useMemo(() => {
    if (isUndefined(startTime) || isUndefined(endTime)) return '';
    const startTimeMinute = startTime?.hour() * 60 + startTime?.minute();
    const endTimeMinute = endTime?.hour() * 60 + endTime?.minute() + (crossDay ? crossDay * 24 * 60 : 0);
    const isHalfDay = endTimeMinute - startTimeMinute <= 4 * 60;
    const shiftType = getShiftType(startTime);
    const startTimeStr = startTime?.format('HHmm');
    const endTimeStr = endTime?.format('HHmm');
    return `${shiftType}-${startTimeStr}-${endTimeStr}${crossDay ? `(+${crossDay})` : ''}-${isHalfDay ? 'H' : 'F'}`;
  }, [startTime, endTime, crossDay, getShiftType]);

  const editModeDefaultStationOptions = React.useMemo(() => {
    if (mode === 'create') return [];
    return editStation ? [{ label: editStation.station_name, value: editStation.station_id }] : [];
  }, [editStation, mode]);

  const displayStationOptions = React.useMemo(() => {
    if (stationIdList?.includes(ADMIN_STATION_FLAG)) {
      return stationOptions.map((item) => ({
        ...item,
        disabled: item.value !== ADMIN_STATION_FLAG,
      }));
    }
    const list = uniqBy([...stationOptions, ...editModeDefaultStationOptions], 'value');
    if (!stationIdList?.length) return list;
    return list.map(item => ({
      ...item,
      disabled: item.value === ADMIN_STATION_FLAG,
    }));
  }, [editModeDefaultStationOptions, stationIdList, stationOptions]);

  const handleConfirm = React.useCallback(() => {
    if (mode === 'create') {
      return handleCreateShift();
    }
    return handleEditShift();
  }, [handleCreateShift, handleEditShift, mode]);

  React.useEffect(() => {
    if (fillMode === 'Auto Fill') {
      form.setFieldValue('slot_code', autoGenerateShiftName);
      if (autoGenerateShiftName) {
        form.validateFields(['slot_code']);
      }
    }
  }, [autoGenerateShiftName, fillMode, form]);

  React.useEffect(() => {
    if (dayjs(startTime).isAfter(dayjs(endTime)) && !crossDay) {
      form.setFieldValue('cross_day', 1);
    }
  }, [startTime, endTime, form, crossDay]);

  const handleCancel = React.useCallback(() => {
    setVisible(false);
    setFillMode('Auto Fill');
    setEditData(undefined);
    form.resetFields();
  }, [form]);

  const shiftModalComp = React.useMemo(() => {
    return (
      <Modal
        title={mode === 'create' ? $gt('Create Shift') : $gt('Edit Shift')}
        width={540}
        visible={visible}
        onCancel={handleCancel}
        onOk={handleConfirm}
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            required
            label={$gt('Shift Name')}
          >
            <Layout.Blocks direction="vertical">
              <Radio.Group
                value={fillMode}
                onChange={(e) => {
                  setFillMode(e.target.value as FillMode);
                }}
              >
                <Radio
                  value="Auto Fill"
                  disabled={mode === 'edit'}
                  hint={$gt('Shift Name will be auto-generated based on shift time.')}
                >
                  { $gt('Auto Fill') }
                </Radio>
                <Radio value="Manual Input">
                  { $gt('Manual Input') }
                </Radio>
              </Radio.Group>
              <Form.Item
                name="slot_code"
                className={style['shift-name-input']}
                rules={[{ required: true, message: $gt('Shift Name is required') }]}
              >
                <Input
                  style={{ width: 420 }}
                  placeholder={$gt('D/N + Start Time + End Time + Staff Type')}
                  disabled={fillMode === 'Auto Fill'}
                />
              </Form.Item>
            </Layout.Blocks>
          </Form.Item>
          <div className={style['time-picker-wrapper']}>
            <Form.Item
              required
              name="start_time"
              label={$gt('Start Time')}
              style={{ width: 160 }}
              wrapperStyle={{ width: 160 }}
              rules={[{ required: true, message: $gt('Start Time is required') }]}
            >
              <TimePicker
                format="HH:mm"
                disabled={mode === 'edit'}
                placeholder={$gt('Select')}
                onChange={(e) => {
                  form.setFieldValue('start_time', e);
                }}
              />
            </Form.Item>
            <div className={style['end-time-wrapper']}>
              <Form.Item
                required
                name="end_time"
                label={$gt('End Time')}
                rules={[{ required: true, message: $gt('End Time is required') }]}
              >
                <TimePicker
                  disabled={mode === 'edit'}
                  format="HH:mm"
                  placeholder={$gt('Select')}
                />
              </Form.Item>
              <Form.Item
                name="cross_day"
                className={style['cross-day-input']}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }
                      if (!/^\d+$/.test(value)) {
                        return Promise.reject($gt('Cross Day must be a positive integer'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  max={grayStationData.shift_max_cross_day ?? DEFAULT_MAX_CROSS_DAY}
                  showControls={false}
                  disabled={mode === 'edit'}
                  suffix={$gt('Cross Day')}
                />
              </Form.Item>
            </div>
          </div>
          <Form.Item
            name="break_time"
            label={$gt('Break Time')}
            wrapperStyle={{ width: 320 }}
          >
            <InputNumber
              showControls={false}
              precision={1}
              max={6}
              min={0}
              suffix={$gt('Hours')}
            />
          </Form.Item>
          <Form.Item
            required
            name="staff_type"
            label={$gt('Staff Type')}
            rules={[{ required: true, message: $gt('Staff Type is required') }]}
          >
            <Radio.Group disabled={mode === 'edit'} options={staffTypeOption} />
          </Form.Item>
          {isAdmin() ? (
            <Form.Item
              required
              name="station_id_list"
              label={$gt('Station')}
              wrapperStyle={{ width: 320 }}
              rules={[{ required: true, message: $gt('Station is required') }]}
            >
              <Select
                allowClear
                showSearch
                mode="multiple"
                disabled={mode === 'edit'}
                options={displayStationOptions ?? editModeDefaultStationOptions}
                loading={stationLoading}
                filterOption={false}
                onSearch={(value) => {
                  return getStationList(value);
                }}
                onDropdownVisibleChange={(visible) => {
                  return onStationDropdownVisibleChangeRef.current(visible);
                }}
              />
            </Form.Item>
          ) : null}
        </Form>
      </Modal>
    );
  }, [displayStationOptions, editModeDefaultStationOptions, fillMode, form, getStationList, grayStationData.shift_max_cross_day, handleCancel, handleConfirm, mode, onStationDropdownVisibleChangeRef, staffTypeOption, stationLoading, visible]);

  const getCurDateByHMS = React.useCallback((hms: string) => {
    const [hour, minute, second] = hms.split(':');
    const actualHour = Number(hour) >= 24 ? Number(hour) - 24 : Number(hour);
    const crossDay = Math.floor(Number(hour) / 24);
    const date = dayjs().startOf('day');
    const newDate = date.add(actualHour, 'hour').add(Number(minute), 'minute').add(Number(second), 'second');
    return {
      date: newDate,
      crossDay,
    };
  }, []);

  const showShiftModal = React.useCallback((mode: 'create' | 'edit', row?: Record<string, any>) => {
    let stationIdList = undefined;
    setMode(mode);
    setVisible(true);
    setEditData(row);
    if (mode === 'edit') {
      stationIdList = isAdmin() ? [row?.station?.station_id] : [getCurrentStationId()];
    }
    setEditStation({ station_name: row?.station?.station_name, station_id: row?.station?.station_id });
    form.setFieldsValue({
      slot_code: row?.slot_code,
      start_time: row?.start_time ? getCurDateByHMS(row.start_time).date : undefined,
      end_time: row?.end_time ? getCurDateByHMS(row.end_time).date : undefined,
      cross_day: row?.end_time ? getCurDateByHMS(row.end_time).crossDay : undefined,
      break_time: row?.break_time,
      staff_type: row?.staff_type,
      station_id_list: stationIdList,
    });
  }, [form, getCurDateByHMS]);

  return {
    showShiftModal,
    shiftModalComp,
  };
};
